import type { ImageModel } from 'ai';
import {
    BaseImageProvider,
    type ImageProviderAdapter,
    type ImageGenerateParams,
    TaskStatus,
    type CreateTaskConfig,
    type CreateTaskResult,
    type PollTaskConfig,
    type PollTaskResult,
    type ProviderContext,
} from '../base';
import { getValueByPath, isStatusMatch, needsTranslation } from '../tools';
import { mapResolutionToSize } from '../../shared/image-size';
import { translateToEnglish } from '../../shared/translate';
import type {
    GeneralImageModelConfig,
    GeneralImageProviderOptions,
    GeneralImageStatusMapping,
    InputPayloadType,
    Resolution,
    TextSize,
} from './types';

export type {
    GeneralImageModelConfig,
    GeneralImageProviderOptions,
    GeneralImageStatusMapping,
    InputPayloadType,
    ImageModelInputBuilder,
    ImageApiPathBuilder,
    ImagePollApiPathBuilder,
    Resolution,
    TextSize,
} from './types';

const REQUIRED_MAPPING_KEYS: (keyof GeneralImageStatusMapping)[] = [
    'taskIdPath',
    'statePath',
    'success',
    'failed',
    'processing',
    'imageUrlPath',
    'errorPath',
];

/**
 * 合并提供商默认状态映射与模型级覆盖，并校验完整性
 */
function resolveStatusMapping(
    providerName: string,
    modelId: string,
    base?: GeneralImageStatusMapping,
    override?: Partial<GeneralImageStatusMapping>
): GeneralImageStatusMapping {
    const merged = { ...base, ...override } as GeneralImageStatusMapping;
    const missing = REQUIRED_MAPPING_KEYS.filter((key) => merged[key] === undefined);
    if (missing.length > 0) {
        throw new Error(
            `[${providerName}][${modelId}] Incomplete status mapping, missing: ${missing.join(', ')}`
        );
    }
    return merged;
}

/**
 * 轮询/结果路径占位符替换：
 * 支持 '${requestId}' / '{taskId}' / '{id}'，无占位符时追加 '/<taskId>'
 */
function resolvePathPlaceholder(path: string, taskId: string): string {
    const placeholders = ['${requestId}', '{taskId}', '{id}'];
    for (const placeholder of placeholders) {
        if (path.includes(placeholder)) {
            return path.split(placeholder).join(taskId);
        }
    }
    return `${path}/${taskId}`;
}

/**
 * 从任意取值中提取图像 URL 列表。
 * 兼容 string / string[] / [{ url }] / [{ resultUrl }] / { resultUrls } / { urls } / { url } 等多种格式。
 */
function extractImageUrlsFromValue(urlValue: any): string[] {
    if (typeof urlValue === 'string') {
        // 仅对形如 JSON 的字符串尝试解析，避免 '123' / 'null' 等被 JSON.parse 吞掉
        const trimmed = urlValue.trim();
        if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
            try {
                return extractImageUrlsFromValue(JSON.parse(trimmed));
            } catch {
                return [];
            }
        }
        return trimmed ? [trimmed] : [];
    }
    if (Array.isArray(urlValue)) {
        if (urlValue.length > 0 && typeof urlValue[0] === 'object') {
            return (urlValue as any[])
                .map((item: any) => item?.url ?? item?.resultUrl)
                .filter((url: any): url is string => typeof url === 'string' && url.length > 0);
        }
        return urlValue.filter((url: any): url is string => typeof url === 'string' && url.length > 0);
    }
    if (urlValue && typeof urlValue === 'object') {
        if (Array.isArray((urlValue as any).resultUrls)) {
            return extractImageUrlsFromValue((urlValue as any).resultUrls);
        }
        if (Array.isArray((urlValue as any).urls)) {
            return extractImageUrlsFromValue((urlValue as any).urls);
        }
        if (typeof (urlValue as any).url === 'string') {
            return [(urlValue as any).url];
        }
    }
    return [];
}

/**
 * 按状态映射从响应中提取图像 URL 列表（支持 imageUrlJsonPath 二次取值）
 */
function extractImageUrls(data: any, mapping: GeneralImageStatusMapping): string[] {
    let raw = getValueByPath(data, mapping.imageUrlPath);
    if (mapping.imageUrlJsonPath !== undefined && typeof raw === 'string') {
        try {
            const parsed = JSON.parse(raw);
            raw = getValueByPath(parsed, mapping.imageUrlJsonPath);
        } catch {
            return [];
        }
    }
    return extractImageUrlsFromValue(raw);
}

/**
 * 从 Provider Options 中获取参考图 URL 列表
 */
function getImageInputUrls(params: ImageGenerateParams): string[] {
    return (params.providerOptions as any)?.url?.image_input ?? [];
}

/**
 * 大小写不敏感地删除请求头（FormData 请求需剔除 Content-Type，交由 fetch 生成 boundary）
 */
function deleteHeaderIgnoreCase(headers: Record<string, string>, name: string): void {
    const lower = name.toLowerCase();
    for (const key of Object.keys(headers)) {
        if (key.toLowerCase() === lower) {
            delete headers[key];
        }
    }
}

/**
 * 默认请求头：Bearer 鉴权
 */
function defaultHeaders(context: ProviderContext): Record<string, string> {
    return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${context.apiKey}`,
    };
}

/**
 * 默认轮询配置
 */
const DEFAULT_POLLING = {
    timeoutMs: 10 * 60 * 1000,
    backoff: {
        initialDelayMs: 5000,
        maxSteps: 20,
        scale: 1,
        jitter: 0.2,
        maxDelayMs: 10000,
    },
};

/**
 * 创建通用图片提供商（配置驱动，无需为每个渠道复制适配器代码）。
 *
 * 对齐 video/general 的 createGeneralVideoProvider，并补充图片特有能力：
 * - 同步模型：创建响应直接返回图片 URL（immediateImageUrls）或二进制（immediateImages）
 * - FormData 请求体：本地文件上传类接口（自动剔除 Content-Type）
 * - 动态任务创建路径（hasImage：generations / edits / compositions）
 * - 二步结果拉取（resultApiPath：轮询成功后再请求一次获取图像）
 * - 轮询路径占位符（'{taskId}' / '{id}' / '${requestId}'）或自定义函数；
 *   pollApiPath 省略时直接把 taskId 当作完整轮询 URL
 * - 提供商级状态映射 + 模型级局部覆盖
 * - success 为空数组时按 imageUrlPath 是否取到 URL 判断成功（适配无状态字段的响应）
 * - failed 为空数组时按 errorPath 是否有值判断失败
 * - 图像结果兼容 string / string[] / [{ url }] / [{ resultUrl }] / { resultUrls } / { urls } /
 *   JSON 字符串（配合 imageUrlJsonPath 二次解析）
 * - 自定义鉴权请求头（非 Bearer）与创建请求额外请求头
 * - 按模型覆盖 baseUrlEnv / apiKeyEnv（实例自动缓存）
 * - 提示词自动翻译（enableTranslation 开启时）
 *
 * @example
 * const myProvider = createGeneralImageProvider({
 *     providerName: 'MYPROVIDER',
 *     baseUrlEnv: 'MYPROVIDER_BASE_URL',
 *     apiKeyEnv: 'MYPROVIDER_API_KEY',
 *     models: {
 *         'my-model': {
 *             apiPath: ({ hasImage }) => hasImage ? '/v1/images/edits' : '/v1/images/generations',
 *             synchronous: true,
 *             statusMapping: {
 *                 taskIdPath: 'task_id',
 *                 statePath: 'status',
 *                 success: [],
 *                 failed: [],
 *                 processing: [],
 *                 imageUrlPath: 'data',
 *                 errorPath: 'message',
 *             },
 *             buildInput: ({ prompt, image_urls }) => ({ prompt, images: image_urls }),
 *         },
 *     },
 * });
 *
 * // 注册模型时：myProvider('my-model')
 */
export function createGeneralImageProvider(
    options: GeneralImageProviderOptions
): (modelId: string) => ImageModel {
    const buildHeaders = options.buildHeaders ?? defaultHeaders;
    const polling = options.polling ?? DEFAULT_POLLING;

    // 按 env 组合缓存 BaseImageProvider 实例（支持模型级 env 覆盖）
    const providerCache = new Map<string, BaseImageProvider>();
    const getOrCreateProvider = (baseUrlEnv: string, apiKeyEnv: string): BaseImageProvider => {
        const cacheKey = `${baseUrlEnv}::${apiKeyEnv}`;
        let provider = providerCache.get(cacheKey);
        if (!provider) {
            provider = new BaseImageProvider(
                {
                    baseUrlEnv,
                    apiKeyEnv,
                    debugEnv: options.debugEnv,
                    providerName: options.providerName,
                },
                polling
            );
            providerCache.set(cacheKey, provider);
        }
        return provider;
    };

    const getModelConfig = (modelId: string): GeneralImageModelConfig => {
        const config = options.models[modelId];
        if (!config) {
            throw new Error(`[${options.providerName}] Model ${modelId} not found`);
        }
        return config;
    };

    // 状态映射惰性缓存：merge + 校验只在每个模型首次使用时执行一次
    const mappingCache = new Map<string, GeneralImageStatusMapping>();
    const getMapping = (modelId: string): GeneralImageStatusMapping => {
        let mapping = mappingCache.get(modelId);
        if (!mapping) {
            const config = getModelConfig(modelId);
            mapping = resolveStatusMapping(
                options.providerName, modelId, options.statusMapping, config.statusMapping
            );
            mappingCache.set(modelId, mapping);
        }
        return mapping;
    };

    // 构建输入载荷（含便捷字段与可选提示词翻译）
    const buildInputPayload = async (
        modelId: string,
        config: GeneralImageModelConfig,
        params: ImageGenerateParams
    ): Promise<InputPayloadType | FormData> => {
        // 从 Provider Options 中获取 image_input
        const image_urls = getImageInputUrls(params);

        // 分辨率：仅读取 providerOptions.image_resolution，非法值兜底 '4K'
        // 兼容 '1K'...'8K'（分辨率档位）与 '1x'...'8x'（超分倍率）
        const image_resolution: Resolution = (() => {
            const v = (params.providerOptions as any)?.image_resolution;
            if (typeof v !== 'string') return '4K';
            const raw = v.trim();
            const up = raw.toUpperCase();
            if (up === '1K' || up === '2K' || up === '3K' || up === '4K' || up === '8K') return up as Resolution;
            const low = raw.toLowerCase();
            if (low === '1x' || low === '2x' || low === '4x' || low === '8x') return low as Resolution;
            return '4K';
        })();

        // 比例: 需要类型转换
        const pixelSize = params.size === 'match' ? undefined : (params.size as `${number}x${number}`);
        const image_size: TextSize = mapResolutionToSize(pixelSize, 'text');

        // 提示词翻译
        let prompt = params.prompt;
        if (config.enableTranslation && needsTranslation(prompt)) {
            try {
                prompt = await translateToEnglish(prompt);
            } catch (error) {
                // 翻译失败时使用原始提示词
                console.error(`[${options.providerName}][${modelId}] Failed to translate prompt:`, error);
            }
        }

        // 获取生成图片数量
        // 优先从 providerOptions._imagesCount 获取（当 useRequestBodyForCount 为 true 时）
        // 否则从 params.n 获取（AI SDK 会使用这个值）
        const imagesCount = (params.providerOptions as any)?._imagesCount ?? params.n;

        return config.buildInput({
            prompt: prompt,
            image_urls: image_urls,
            size: params.size,
            aspectRatio: params.aspectRatio,
            seed: params.seed,
            image_size: image_size,
            image_resolution: image_resolution,
            providerOptions: params.providerOptions,
            n: imagesCount,
            params: params,
        });
    };

    // 通用适配器
    const adapter: ImageProviderAdapter = {
        // 构建**任务创建**请求
        async buildCreateTaskRequest(modelId, params, context): Promise<CreateTaskConfig> {
            const config = getModelConfig(modelId);
            // 构建输入参数（可能是异步构建）
            const payload = await buildInputPayload(modelId, config, params);
            // 解析任务创建路径（字符串或按是否有图像输入动态切换）
            const apiPath = typeof config.apiPath === 'string'
                ? config.apiPath
                : config.apiPath({ hasImage: getImageInputUrls(params).length > 0, params });
            const headers: Record<string, string> = {
                ...buildHeaders(context),
                ...config.createHeaders,
            };
            // FormData 请求体：剔除 Content-Type，交由 fetch 自动生成 multipart boundary
            if (payload instanceof FormData) {
                deleteHeaderIgnoreCase(headers, 'Content-Type');
            }
            // 返回请求配置
            return {
                url: `${context.baseUrl}${apiPath}`,
                method: 'POST',
                headers: headers,
                body: payload,
                responseType: config.responseType,
            };
        },

        // 解析**任务创建**响应
        parseCreateTaskResponse(modelId, response, data): CreateTaskResult {
            const config = getModelConfig(modelId);
            const mapping = getMapping(modelId);

            // 同步模型：创建响应直接返回最终结果
            if (config.synchronous === true) {
                // 1) 直出图片二进制（content-type: image/* 或 responseType 强制 arrayBuffer）
                if (data instanceof Uint8Array) {
                    if (data.length === 0) {
                        return {
                            error: 'Empty image buffer in synchronous response',
                            raw: { contentType: response.headers.get('content-type') },
                        };
                    }
                    return {
                        immediateImages: [data],
                        raw: { contentType: response.headers.get('content-type'), byteLength: data.length },
                    };
                }

                // 2) 依据状态判断（配置了 success / failed 状态值时）
                const state = getValueByPath(data, mapping.statePath);
                if (state !== undefined && mapping.failed.length > 0 && isStatusMatch(state, mapping.failed)) {
                    const error = getValueByPath(data, mapping.errorPath);
                    return { error: error != null && error !== '' ? String(error) : 'unknown error', raw: data };
                }
                const stateOk = state === undefined
                    || mapping.success.length === 0
                    || isStatusMatch(state, mapping.success);
                if (stateOk) {
                    // 3) 提取图片 URL（无状态字段时直接按结果字段判断成功）
                    const imageUrls = extractImageUrls(data, mapping);
                    if (imageUrls.length > 0) {
                        return { immediateImageUrls: imageUrls, raw: data };
                    }
                }
                // 4) 无结果：按错误响应处理
                const createError = getValueByPath(data, mapping.createErrorPath ?? mapping.errorPath);
                if (createError) {
                    return { error: String(createError), raw: data };
                }
                // 否则退回异步流程（提取 taskId 开始轮询）
            }

            // 获取任务 ID（异步）
            const taskId = getValueByPath(data, mapping.taskIdPath);
            if (!taskId) {
                const createError = getValueByPath(data, mapping.createErrorPath ?? mapping.errorPath);
                if (createError) {
                    return { error: String(createError), raw: data };
                }
                throw new Error(`Failed to get task id from response`);
            }
            return {
                taskId: taskId,
                raw: data,
            };
        },

        // 构建**轮询任务**请求
        buildPollTaskRequest(modelId, taskId, params, context): PollTaskConfig {
            const config = getModelConfig(modelId);
            const pollApiPath = config.pollApiPath;
            return {
                url: (taskId) => {
                    // polling_url 模式：直接使用 taskId 作为完整 URL
                    if (!pollApiPath) {
                        return taskId;
                    }
                    const path = typeof pollApiPath === 'string'
                        ? resolvePathPlaceholder(pollApiPath, taskId)
                        : pollApiPath({ taskId, params });
                    return `${context.baseUrl}${path}`;
                },
                method: 'GET',
                headers: buildHeaders(context),
            };
        },

        // 解析**轮询任务**响应
        parsePollTaskResponse(modelId, response, data): PollTaskResult {
            const config = getModelConfig(modelId);
            const mapping = getMapping(modelId);
            // 获取任务状态
            const state = getValueByPath(data, mapping.statePath);
            // 如果状态值未找到则视为**处理中**
            if (state === undefined) {
                return {
                    status: TaskStatus.PROCESSING,
                    raw: data,
                };
            }
            // 1. 判断**成功**状态
            if (mapping.success.length === 0) {
                // success 为空数组：改用 imageUrlPath 是否取到 URL 判断成功
                const imageUrls = extractImageUrls(data, mapping);
                if (imageUrls.length > 0) {
                    return {
                        status: TaskStatus.SUCCESS,
                        imageUrls: imageUrls,
                        raw: data,
                    };
                }
            } else if (isStatusMatch(state, mapping.success)) {
                // 有 resultApiPath → 需要二次拉取，返回空 imageUrls，基类会调用 buildFetchResultRequest
                if (config.resultApiPath) {
                    return { status: TaskStatus.SUCCESS, imageUrls: [], raw: data };
                }
                const imageUrls = extractImageUrls(data, mapping);
                // 图像 URL 不存在则视为**失败**状态
                if (imageUrls.length === 0) {
                    return {
                        status: TaskStatus.FAILED,
                        error: 'Missing image URLs in result',
                        raw: data,
                    };
                }
                return {
                    status: TaskStatus.SUCCESS,
                    imageUrls: imageUrls,
                    raw: data,
                };
            }
            // 2. 判断**失败**状态
            const error = getValueByPath(data, mapping.errorPath);
            if (mapping.failed.length === 0) {
                // failed 为空数组：改用 errorPath 字段是否有值来判断失败
                if (error !== undefined && error !== null && error !== '') {
                    return {
                        status: TaskStatus.FAILED,
                        error: String(error),
                        raw: data,
                    };
                }
            } else if (isStatusMatch(state, mapping.failed)) {
                return {
                    status: TaskStatus.FAILED,
                    error: error != null && error !== '' ? String(error) : 'unknown error',
                    raw: data,
                };
            }
            // 3. 判断**处理中**状态（含未知状态兜底）
            return {
                status: TaskStatus.PROCESSING,
                raw: data,
            };
        },

        // 构建**结果拉取**请求（二步轮询）
        buildFetchResultRequest(modelId, taskId, context): CreateTaskConfig | null {
            const config = getModelConfig(modelId);
            if (!config.resultApiPath) return null;

            return {
                url: `${context.baseUrl}${resolvePathPlaceholder(config.resultApiPath, taskId)}`,
                method: 'GET',
                headers: buildHeaders(context),
            };
        },

        // 解析**结果拉取**响应（成功 / 失败均处理）
        parseFetchResultResponse(modelId, response, data): PollTaskResult {
            const mapping = getMapping(modelId);

            if (!response.ok) {
                const error = getValueByPath(data, mapping.errorPath);
                return { status: TaskStatus.FAILED, error: error ? String(error) : `HTTP ${response.status}`, raw: data };
            }

            const imageUrls = extractImageUrls(data, mapping);
            if (imageUrls.length === 0) {
                return { status: TaskStatus.FAILED, error: 'Missing image URLs in result response', raw: data };
            }
            return { status: TaskStatus.SUCCESS, imageUrls: imageUrls, raw: data };
        },
    };

    // 工厂函数：按 modelId 创建 ImageModel
    return (modelId: string): ImageModel => {
        const config = getModelConfig(modelId);
        // 提前校验状态映射完整性，配置错误在注册期即可暴露（同时填充缓存）
        getMapping(modelId);
        const provider = getOrCreateProvider(
            config.baseUrlEnv ?? options.baseUrlEnv,
            config.apiKeyEnv ?? options.apiKeyEnv
        );
        return provider.createImageModel(modelId, adapter, config.timeoutMs);
    };
}
